import React, { useMemo, useState } from "react";
import { Alert, Button, Col, Container, Form, Modal, Row } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import JobPost from "./JobPost";
import jobPosts from "../jobPosts.json";

const JobDetailsPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [attachments, setAttachments] = useState([]);
  const [status, setStatus] = useState({ state: "idle", message: "" });
  const [showModal, setShowModal] = useState(false);
  const MAX_TOTAL_SIZE_MB = 10;

  const selectedJob = useMemo(
    () => jobPosts.find((job) => job.id === jobId),
    [jobId]
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
    const totalMb = totalBytes / (1024 * 1024);

    if (totalMb > MAX_TOTAL_SIZE_MB) {
      setStatus({
        state: "error",
        message: `Total attachment size exceeds ${MAX_TOTAL_SIZE_MB} MB. Please remove some files.`,
      });
      return;
    }

    setStatus({ state: "idle", message: "" });
    setAttachments(files);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!selectedJob) {
      return;
    }

    if (!formData.name.trim() || !formData.email.trim()) {
      setStatus({
        state: "error",
        message: "Name and email are required.",
      });
      return;
    }

    if (attachments.length === 0) {
      setStatus({
        state: "error",
        message: "Please add at least one attachment (CV).",
      });
      return;
    }

    const totalBytes = attachments.reduce((sum, file) => sum + file.size, 0);
    const totalMb = totalBytes / (1024 * 1024);
    if (totalMb > MAX_TOTAL_SIZE_MB) {
      setStatus({
        state: "error",
        message: `Total attachment size exceeds ${MAX_TOTAL_SIZE_MB} MB. Please remove some files.`,
      });
      return;
    }

    const subject = encodeURIComponent(
      `Application for ${selectedJob.title} (${selectedJob.id})`
    );

    const body = encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nRole: ${selectedJob.title}\n\nMessage:\n${formData.message}\n\nPlease find my resume attached.`
    );

    const mailtoLink = `mailto:careers@ecorvi.com?subject=${subject}&body=${body}`;

    setStatus({
      state: "info",
      message:
        "Your email client should open. Please attach the selected files (CV) before sending.",
    });
    window.location.href = mailtoLink;
    setShowModal(false);
  };

  return (
    <Container className="py-4 py-md-5">
      <Row>
        <Col className="my-4">
          <Button
            variant="link"
            className="d-inline-flex align-items-center p-0 text-decoration-none fw-semibold"
            onClick={() => navigate("/career")}
            style={{ color: "rgb(16, 16, 112)" }}
          >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                fill="currentColor"
                className="bi bi-arrow-left-square me-2"
                viewBox="0 0 16 16"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M15 2a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1zM2 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2z"
                />
                <path
                  fillRule="evenodd"
                  d="M11.5 8a.5.5 0 0 0-.5-.5H5.707l2.147-2.146a.5.5 0 1 0-.708-.708l-3 3a.5.5 0 0 0 0 .708l3 3a.5.5 0 0 0 .708-.708L5.707 8.5H11a.5.5 0 0 0 .5-.5"
                />
              </svg>
              Back to Careers
          </Button>
        </Col>
      </Row>
      <JobPost jobId={jobId} />
      {/* <Button variant="primary" size="lg" className="d-block mx-auto" onClick={() => setShowModal(true)}>
            Apply
          </Button> */}
      
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Apply for {selectedJob?.title || "this role"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {status.state === "info" ? (
            <Alert variant="info">{status.message}</Alert>
          ) : null}
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3" controlId="applicantName">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your full name"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="applicantEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="applicantPhone">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="Preferred contact number"
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="applicantMessage">
              <Form.Label>Message</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Short note about your fit or availability"
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="applicantResume">
              <Form.Label>Attach CV</Form.Label>
              <Form.Control
                type="file"
                multiple
                onChange={handleFileChange}
                required
              />
              <Form.Text muted>
                Multiple files allowed. Total size must be under {MAX_TOTAL_SIZE_MB} MB. You will need to attach them again when your email client opens.
              </Form.Text>
            </Form.Group>
            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                Send application
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default JobDetailsPage;
